import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import produce, { castDraft, Draft } from 'immer';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutateUserRolePreferences } from '../../../../api/userrolepreferences/hooks';
import { UserRolePreferencesSettingsSettings } from '../../../../api/userrolepreferences/types';
import {
  useExecutorRoleContext,
  useUserRolePreferencesSettingsContext,
} from '../../../../context/contexts';
import { GlobalFilterTypeHandle } from '../../../../context/types';
import { uuid } from '../../../utils';
import { FilterPills } from '../filterbar/FilterPills';
import { Filter, FilterTypes } from '../filterbar/types';
import { defaultOptions } from '../filterbar/utils-display';
import { FilterSection } from '../types';
import { NameValidationError } from './NameValidationError';
import { DialogHandle, MyFiltersGroup } from './types';

interface SaveDialogProps<T extends FilterTypes> {
  filtersHandle: GlobalFilterTypeHandle<T>;
  group?: MyFiltersGroup<T>;
  dialogHandle: DialogHandle;
  setExpand: (b: boolean) => void;
  filterSections: FilterSection[];
}

const getNewGroups = <T extends FilterTypes>(
  group: MyFiltersGroup<T> | undefined,
  groupName: string,
  groups: MyFiltersGroup<T>[],
  executorRoleId: string,
  filters: T[]
): MyFiltersGroup<T>[] => {
  let newGroups: MyFiltersGroup<T>[] = [];
  if (group) {
    const existingGroupWithSameName = groups.some(
      (g) => g.id !== group.id && g.name === group.name
    );
    if (existingGroupWithSameName) {
      throw new NameValidationError();
    } else {
      newGroups = groups.map((g) =>
        g.id === group.id ? { ...g, name: groupName } : g
      );
    }
  } else {
    const existingGroup = groups.some((g) => g.name === groupName);
    if (existingGroup) {
      throw new NameValidationError();
    } else {
      const newGroup = { id: uuid(), name: groupName, filters, executorRoleId };
      newGroups = [...groups, newGroup];
    }
  }
  return newGroups;
};

export const SaveDialog = <T extends FilterTypes>(
  props: SaveDialogProps<T>
) => {
  const { t } = useTranslation();
  const { filtersHandle, group, dialogHandle, setExpand, filterSections } =
    props;
  const [open, setOpen] = dialogHandle;
  const [saveError, setSaveError] = useState('');
  const [groupName, setGroupName] = useState(group?.name ?? '');
  const executorRole = useExecutorRoleContext();
  const [filtersState] = filtersHandle;
  const userRolePreferencesService = useMutateUserRolePreferences<Filter>();

  useEffect(() => {
    setGroupName(group?.name ?? '');
  }, [group]);

  const currentRolePreferences = useUserRolePreferencesSettingsContext();
  const groups = currentRolePreferences?.settings?.myFilterGroups ?? [];
  const groupsForCurrentExecutorRole = groups.filter(
    (g) => g.executorRoleId === executorRole.id
  );

  const handleClose = () => {
    setOpen(false);
    setSaveError('');
    setGroupName(group?.name ?? '');
  };

  const onSubmit = () => {
    setSaveError('');
    try {
      const newGroups = getNewGroups(
        group,
        groupName,
        groupsForCurrentExecutorRole,
        executorRole.id,
        filtersState.items
      );
      const newSettings = produce<
        UserRolePreferencesSettingsSettings<Filter>,
        Draft<UserRolePreferencesSettingsSettings<Filter>>
      >(
        currentRolePreferences?.settings ?? {
          myFilterGroups: [] as MyFiltersGroup<Filter>[],
          profile: {},
        },
        (draft: Draft<UserRolePreferencesSettingsSettings<Filter>>) => {
          draft.myFilterGroups = castDraft(newGroups);
        }
      );
      userRolePreferencesService
        .mutateAsync(newSettings)
        .then(() => {
          handleClose();
          setExpand(true);
        })
        .catch(() =>
          setSaveError(
            t('common:filterTray.saveError', { name: group?.name ?? 'unknown' })
          )
        );
    } catch (e) {
      setSaveError(
        t('common:filterTray.saveError', {
          name: group?.name ?? groupName ?? 'unknown',
        })
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>
        {group
          ? `${t('common:filterTray.editFilterGroupName')}: ${groupName}`
          : t('common:filterTray.saveFilterGroup')}
      </DialogTitle>
      <DialogContent>
        <TextField
          error={!!saveError}
          helperText={saveError}
          autoFocus
          margin="dense"
          id="filterGroup"
          name="name"
          label={t('common:filterTray.typeNameHere')}
          variant="outlined"
          fullWidth
          value={groupName}
          onChange={(e) => {
            setGroupName(e.target.value);
            setSaveError('');
          }}
        />
        <Grid container item pt={2}>
          <Typography>{t('common:filterTray.selectedFilters')}</Typography>
          <Grid container item>
            <FilterPills<T>
              filtersHandle={filtersHandle}
              filterSections={filterSections}
              options={{ ...defaultOptions, allowDelete: false }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ paddingBottom: '1em' }}>
        <Button
          autoFocus
          variant="contained"
          disabled={!!saveError || !groupName}
          onClick={onSubmit}
        >
          {t('common:filterTray.save')}
        </Button>
        <Button onClick={handleClose} variant="outlined">
          {t('common:filterTray.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
